import { useState, useMemo } from 'react';
import { Split, Plus, Trash2, Pencil, Package, Check, Search } from 'lucide-react';
import Button from '@/components/ui/Button';
import Input from '@/components/ui/Input';
import Modal from '@/components/ui/Modal';
import { Product } from '@/types';
import { useProducts, useCreateProduct, useUpdateProduct, useDeleteProduct } from '@/hooks/useProducts';
import { formatPrice } from '@/utils/format';
import toast from 'react-hot-toast';

interface FracForm {
  name: string;
  code: string;
  barcode: string;
  source_product_id?: number;
  grams: string;   // gramos por paquete
  price: string;   // precio de venta del paquete
}

// Una fila del alta múltiple: cada una termina siendo un producto fraccionado
interface FracRow {
  id: number;
  name: string;
  amount: string;              // cantidad tal cual la escribieron
  unit: 'g' | 'kg';            // en qué unidad la escribieron
  price: string;
  barcode: string;
  nameTouched: boolean;  // si el usuario editó el nombre, no se autocompleta más
}

// Gramos reales de una fila (adentro siempre se trabaja en gramos)
const rowGrams = (row: FracRow): number => {
  const n = parseFloat((row.amount || '').replace(',', '.'));
  if (isNaN(n) || n <= 0) return 0;
  return row.unit === 'kg' ? n * 1000 : n;
};

// Presentaciones típicas, para cargar rápido
const GRAM_PRESETS = [250, 500, 1000, 2000, 5000, 15000];

// "Alimento X 500 g" / "Alimento X 1 kg"
const packageLabel = (parentName: string, grams: number): string => {
  if (!grams) return parentName;
  const size = grams >= 1000 && grams % 100 === 0
    ? `${(grams / 1000).toLocaleString('es-AR', { maximumFractionDigits: 2 })} kg`
    : `${grams} g`;
  return `${parentName} ${size}`;
};

const emptyForm: FracForm = {
  name: '',
  code: '',
  barcode: '',
  source_product_id: undefined,
  grams: '',
  price: '',
};

const num = (v: string): number => {
  const n = parseFloat((v || '').replace(',', '.'));
  return isNaN(n) ? 0 : n;
};

// Formatea un número lindo (es-AR), sin el signo $
const fmtNum = (n: number): string =>
  n.toLocaleString('es-AR', { maximumFractionDigits: 2 });

// Genera un código de barras interno EAN-13 (prefijo 200 = uso interno)
const genBarcode = (): string => {
  let base = '200';
  for (let i = 0; i < 9; i++) base += Math.floor(Math.random() * 10);
  let sum = 0;
  for (let i = 0; i < 12; i++) sum += parseInt(base[i], 10) * (i % 2 === 0 ? 1 : 3);
  const check = (10 - (sum % 10)) % 10;
  return base + check;
};

export default function Fraccionados() {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editing, setEditing] = useState<Product | null>(null);
  const [form, setForm] = useState<FracForm>(emptyForm);
  const [deleteConfirm, setDeleteConfirm] = useState<Product | null>(null);
  const [search, setSearch] = useState('');

  // El fraccionado puede ser un paquete nuevo o un producto que ya existe en el catálogo
  // (ahí no se crea nada: se le asigna el padre y pasa a descontar de su stock).
  const [mode, setMode] = useState<'new' | 'existing'>('new');
  const [linkedProduct, setLinkedProduct] = useState<Product | null>(null);

  // Alta de varias fracciones del MISMO padre en una sola pasada: se elige el producto a
  // granel una vez y se cargan todas las presentaciones juntas (250 g, 500 g, 1 kg...).
  const [rows, setRows] = useState<FracRow[]>([]);
  const [nextRowId, setNextRowId] = useState(1);
  const [savingRows, setSavingRows] = useState(false);

  const { data: allProducts = [], isLoading } = useProducts({ only_active: true });

  const createProduct = useCreateProduct();
  const updateProduct = useUpdateProduct();
  const deleteProduct = useDeleteProduct();

  // Fraccionados = productos con un producto padre asignado
  const fraccionados = useMemo(() => {
    const q = search.trim().toLowerCase();
    return allProducts
      .filter((p) => p.source_product_id)
      // Busca por el nombre del paquete, su codigo, su codigo de barras o el padre
      .filter((p) =>
        !q ||
        p.name.toLowerCase().includes(q) ||
        (p.code || '').toLowerCase().includes(q) ||
        (p.barcode || '').toLowerCase().includes(q) ||
        (p.source_product_name || '').toLowerCase().includes(q)
      )
      .sort((a, b) => a.name.localeCompare(b.name, 'es', { sensitivity: 'base' }));
  }, [allProducts, search]);

  // Padres posibles: productos marcados "a granel" (no servicios, no fraccionados)
  const parentOptions = useMemo(
    () =>
      allProducts
        .filter((p) => p.is_bulk && !p.is_service && !p.source_product_id && p.is_active)
        .sort((a, b) => a.name.localeCompare(b.name, 'es', { sensitivity: 'base' })),
    [allProducts]
  );

  const productById = useMemo(() => {
    const m = new Map<number, Product>();
    allProducts.forEach((p) => m.set(p.id, p));
    return m;
  }, [allProducts]);

  const parent = form.source_product_id ? productById.get(form.source_product_id) : undefined;
  const gramsNum = num(form.grams);          // gramos por paquete
  const fractionKg = gramsNum / 1000;        // lo que descuenta del padre (padre en kg)
  const priceNum = num(form.price);

  // Costo del paquete = costo por kg del padre × kg del paquete
  const parentCostPerKg = parent?.cost_price != null ? Number(parent.cost_price) : 0;
  const fracCost = parentCostPerKg * fractionKg;
  const fracProfit = priceNum - fracCost;
  const fracMarkupPct = fracCost > 0 ? (fracProfit / fracCost) * 100 : 0;
  const fracMarginPct = priceNum > 0 ? (fracProfit / priceNum) * 100 : 0;

  // Stock del padre (en kg) y cuántos paquetes salen
  const parentStockKg = parent ? Number(parent.current_stock ?? 0) : 0;
  const possiblePackages = fractionKg > 0 ? Math.floor(parentStockKg / fractionKg) : 0;

  // Productos que se pueden convertir en fraccionado: cualquiera del catálogo que no sea
  // servicio, ni a granel, ni ya fraccionado.
  const linkableProducts = useMemo(
    () =>
      allProducts
        .filter((p) => !p.is_service && !p.is_bulk && !p.source_product_id && p.is_active)
        .sort((a, b) => a.name.localeCompare(b.name, 'es', { sensitivity: 'base' })),
    [allProducts]
  );

  // Al elegir un producto existente, se traen sus datos al formulario
  const onSelectLinked = (p: Product) => {
    setLinkedProduct(p);
    setForm((f) => ({
      ...f,
      name: p.name,
      code: p.code ?? '',
      barcode: p.barcode ?? '',
      price: p.sale_price != null ? String(Number(p.sale_price)) : f.price,
    }));
  };

  // === Alta múltiple ===
  const isMulti = !editing && mode === 'new';

  // Presentaciones que este padre ya tiene cargadas: se muestran al elegirlo, para
  // agregar las que faltan sin repetir ninguna.
  const parentFractions = useMemo(() => {
    if (!parent) return [];
    return allProducts
      .filter((f) => f.source_product_id === parent.id && f.fraction_quantity != null)
      .map((f) => ({ id: f.id, name: f.name, grams: Math.round(Number(f.fraction_quantity) * 1000) }))
      .sort((a, b) => a.grams - b.grams);
  }, [allProducts, parent]);

  const existingGrams = useMemo(
    () => new Set(parentFractions.map((f) => f.grams)),
    [parentFractions]
  );

  const addRow = (grams?: number) => {
    setRows((rs) => [
      ...rs,
      {
        id: nextRowId,
        name: parent && grams ? packageLabel(parent.name, grams) : '',
        amount: grams ? String(grams >= 1000 ? grams / 1000 : grams) : '',
        unit: grams && grams >= 1000 ? 'kg' : 'g',
        price: '',
        barcode: '',
        nameTouched: false,
      },
    ]);
    setNextRowId((n) => n + 1);
  };

  const updateRow = (id: number, patch: Partial<FracRow>) =>
    setRows((rs) => rs.map((r) => (r.id === id ? { ...r, ...patch } : r)));

  const removeRow = (id: number) => setRows((rs) => rs.filter((r) => r.id !== id));

  // Al cambiar los gramos se rearma el nombre, salvo que lo hayan escrito a mano
  const onRowSizeChange = (row: FracRow, patch: { amount?: string; unit?: 'g' | 'kg' }) => {
    const next = { ...row, ...patch };
    const g = rowGrams(next);
    updateRow(row.id, {
      ...patch,
      name: row.nameTouched ? row.name : (parent && g > 0 ? packageLabel(parent.name, g) : row.name),
    });
  };

  const validRows = rows.filter((r) => r.name.trim() && rowGrams(r) > 0 && num(r.price) > 0);

  const openCreate = () => {
    setEditing(null);
    setMode('new');
    setLinkedProduct(null);
    setForm(emptyForm);
    setRows([
      { id: 1, name: '', amount: '', unit: 'g', price: '', barcode: '', nameTouched: false },
    ]);
    setNextRowId(2);
    setIsModalOpen(true);
  };

  const openEdit = (frac: Product) => {
    setEditing(frac);
    setMode('new');       // editando ya hay un producto: el modo no aplica
    setLinkedProduct(null);
    setForm({
      name: frac.name,
      code: frac.code ?? '',
      barcode: frac.barcode ?? '',
      source_product_id: frac.source_product_id ?? undefined,
      // fraction_quantity guardado en kg -> mostrar en gramos
      grams: frac.fraction_quantity != null ? String(Math.round(Number(frac.fraction_quantity) * 1000)) : '',
      price: frac.sale_price != null ? String(Number(frac.sale_price)) : '',
    });
    setIsModalOpen(true);
  };

  const updateForm = (patch: Partial<FracForm>) => setForm((f) => ({ ...f, ...patch }));

  // Al elegir padre, autocompletar el nombre si está vacío
  const onSelectParent = (id?: number) => {
    const p = id ? productById.get(id) : undefined;
    setForm((f) => ({
      ...f,
      source_product_id: id,
      name: f.name.trim() ? f.name : (p ? p.name : f.name),
    }));
    // Rearmar los nombres del alta múltiple con el padre recién elegido
    if (p) {
      setRows((rs) =>
        rs.map((r) => {
          const g = rowGrams(r);
          return r.nameTouched || g <= 0 ? r : { ...r, name: packageLabel(p.name, g) };
        })
      );
    }
  };

  const isValid = isMulti
    ? !!form.source_product_id && validRows.length > 0
    : form.name.trim().length > 0 &&
      !!form.source_product_id &&
      gramsNum > 0 &&
      priceNum > 0 &&
      (editing !== null || !!linkedProduct);

  // Crea de una sola vez todas las fracciones cargadas para el mismo padre
  const handleSaveRows = async () => {
    if (!parent || validRows.length === 0) return;

    setSavingRows(true);
    const failed: string[] = [];
    let created = 0;

    for (const row of validRows) {
      try {
        await createProduct.mutateAsync({
          name: row.name.trim(),
          barcode: row.barcode.trim() || undefined,
          source_product_id: parent.id,
          fraction_quantity: rowGrams(row) / 1000,
          track_stock: false,
          sale_price: num(row.price),
          category_id: parent.category_id ?? undefined,
          supplier_id: parent.supplier_id ?? undefined,
        });
        created += 1;
      } catch (e: any) {
        const detail = e?.response?.data?.detail;
        failed.push(`${row.name.trim()}: ${typeof detail === 'string' ? detail : 'error al crear'}`);
      }
    }

    setSavingRows(false);

    if (created > 0) {
      toast.success(created === 1 ? 'Fraccionado creado' : `${created} fraccionados creados`);
    }
    if (failed.length > 0) {
      // Quedan en el modal solo los que fallaron, para corregirlos sin recargar todo
      toast.error(failed.join(' · '));
      setRows((rs) => rs.filter((r) => failed.some((f) => f.startsWith(`${r.name.trim()}:`))));
      return;
    }

    setIsModalOpen(false);
    setRows([]);
    setForm(emptyForm);
  };

  const handleSave = async () => {
    if (!isValid || !parent) return;
    if (isMulti) return handleSaveRows();

    const payload = {
      name: form.name.trim(),
      code: form.code.trim() || undefined,
      barcode: form.barcode.trim() || undefined,
      // Fraccionado: consume del padre. El backend fuerza track_stock=false y
      // calcula el costo automáticamente desde el costo del padre.
      source_product_id: form.source_product_id,
      fraction_quantity: fractionKg,   // en kg (lo que descuenta del padre)
      track_stock: false,
      sale_price: priceNum,
      // Un paquete nuevo hereda categoría y proveedor del padre para que agrupen juntos.
      // Si es un producto que ya existía, se le respeta la clasificación que tenía.
      ...(mode === 'existing' && !editing
        ? {}
        : {
            category_id: parent.category_id ?? undefined,
            supplier_id: parent.supplier_id ?? undefined,
          }),
    };

    try {
      if (editing) {
        await updateProduct.mutateAsync({ id: editing.id, data: payload });
        toast.success('Fraccionado actualizado');
      } else if (mode === 'existing' && linkedProduct) {
        // No se crea un producto nuevo: al que ya existía se le asigna el padre. El
        // backend le saca el stock propio y le calcula el costo desde el padre.
        await updateProduct.mutateAsync({ id: linkedProduct.id, data: payload });
        toast.success(`"${linkedProduct.name}" quedó asociado al producto a granel`);
      } else {
        await createProduct.mutateAsync(payload);
        toast.success('Fraccionado creado');
      }
      setIsModalOpen(false);
      setForm(emptyForm);
      setLinkedProduct(null);
      setMode('new');
      setEditing(null);
    } catch (e: any) {
      const msg = e?.response?.data?.detail || e?.message || 'Error al guardar el fraccionado';
      toast.error(typeof msg === 'string' ? msg : 'Error al guardar el fraccionado');
      console.error('Error guardando fraccionado:', e);
    }
  };

  const handleDelete = async () => {
    if (!deleteConfirm) return;
    try {
      await deleteProduct.mutateAsync(deleteConfirm.id);
      toast.success('Fraccionado eliminado');
      setDeleteConfirm(null);
    } catch (e: any) {
      const msg = e?.response?.data?.detail || 'Error al eliminar el fraccionado';
      toast.error(typeof msg === 'string' ? msg : 'Error al eliminar el fraccionado');
      console.error('Error eliminando fraccionado:', e);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
            <Split className="h-6 w-6 text-amber-600" />
            Fraccionados
          </h1>
          <p className="text-gray-500">
            Vendé un producto a granel en paquetes (ej. Alimento 900 g). Descuenta del stock del padre.
          </p>
        </div>
        <Button onClick={openCreate}>
          <Plus className="h-5 w-5 mr-2" />
          Nuevo fraccionado
        </Button>
      </div>

      {/* Buscador */}
      <div className="relative">
        <Search className="absolute left-3 top-3 h-4 w-4 text-gray-400 pointer-events-none" />
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Buscar por nombre, código o producto a granel..."
          className="w-full pl-9 pr-3 py-2.5 border border-gray-300 rounded-xl text-sm focus:outline-none focus:border-primary-400"
        />
      </div>

      {/* Lista */}
      {isLoading ? (
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-gray-600 mx-auto"></div>
        </div>
      ) : fraccionados.length === 0 ? (
        <div className="bg-white rounded-2xl border border-gray-200 text-center py-16">
          <Split className="h-16 w-16 text-gray-300 mx-auto mb-4" />
          <p className="text-gray-600 font-medium">
            {search.trim() ? `Sin resultados para "${search.trim()}"` : 'No hay fraccionados creados'}
          </p>
          <p className="text-gray-400 text-sm">
            {search.trim()
              ? 'Probá con otro nombre o con el producto a granel'
              : 'Elegí un producto a granel y creá paquetes con gramos y precio fijos'}
          </p>
        </div>
      ) : (
        <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
          {/* Encabezado (desktop) */}
          <div className="hidden md:grid grid-cols-[90px_1fr_1fr_130px_140px_110px] items-center gap-3 px-4 py-2.5 bg-gray-50 border-b border-gray-200 text-xs uppercase tracking-wide font-semibold text-gray-500">
            <span>Gramos</span>
            <span>Nombre</span>
            <span>Sale de (padre)</span>
            <span className="text-right">Precio</span>
            <span className="text-right">Stock padre</span>
            <span className="text-right">Acciones</span>
          </div>
          <div className="divide-y divide-gray-100">
            {fraccionados.map((frac) => {
              const padre = frac.source_product_id ? productById.get(frac.source_product_id) : undefined;
              const grams = frac.fraction_quantity != null ? Math.round(Number(frac.fraction_quantity) * 1000) : null;
              const stockKg = Number(frac.source_current_stock ?? padre?.current_stock ?? 0);
              return (
                <div
                  key={frac.id}
                  className="grid grid-cols-2 md:grid-cols-[90px_1fr_1fr_130px_140px_110px] md:items-start items-center gap-x-3 gap-y-1 px-4 py-3"
                >
                  {/* Gramos */}
                  <span className="text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full font-semibold justify-self-start">
                    {grams != null ? `${fmtNum(grams)} g` : 'FRAC'}
                  </span>
                  {/* Nombre */}
                  <div className="min-w-0 col-span-2 md:col-span-1 order-last md:order-none">
                    <p className="font-medium text-gray-800 break-words leading-snug">
                      {frac.name}
                    </p>
                    {frac.code && <p className="text-xs text-gray-400 break-words">{frac.code}</p>}
                  </div>
                  {/* Padre */}
                  <div className="text-sm text-gray-600 flex items-center gap-1 min-w-0">
                    <Package className="h-3.5 w-3.5 text-gray-400 flex-shrink-0" />
                    <span className="break-words leading-snug">{frac.source_product_name ?? padre?.name ?? '—'}</span>
                  </div>
                  {/* Precio */}
                  <p className="text-base font-bold text-green-600 md:text-right justify-self-end md:justify-self-auto">
                    {formatPrice(frac.sale_price ?? 0)}
                  </p>
                  {/* Stock padre */}
                  <div className="text-sm md:text-right">
                    <span className="text-amber-700 font-semibold">{fmtNum(stockKg)} kg</span>
                    <span className="block text-[11px] text-gray-400">
                      ≈ {Number(frac.available_units ?? 0)} u.
                    </span>
                  </div>
                  {/* Acciones */}
                  <div className="flex items-center gap-1.5 md:justify-end col-span-2 md:col-span-1">
                    <button
                      onClick={() => openEdit(frac)}
                      title="Editar"
                      className="p-2 rounded-lg bg-gray-100 hover:bg-gray-200 text-gray-700"
                    >
                      <Pencil className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => setDeleteConfirm(frac)}
                      title="Eliminar"
                      className="p-2 rounded-lg bg-red-50 text-red-500 hover:bg-red-100"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Modal crear/editar */}
      <Modal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        title={editing ? 'Editar fraccionado' : 'Nuevo fraccionado'}
        size="lg"
      >
        <div className="space-y-4">
          {/* Producto padre */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">
              Producto a granel (padre) *
            </label>
            <ProductPicker
              products={parentOptions}
              selected={parent}
              placeholder="Buscar producto a granel"
              onSelect={(p) => onSelectParent(p.id)}
              onClear={() => onSelectParent(undefined)}
            />
            {parentOptions.length === 0 && (
              <p className="text-xs text-amber-600 mt-1">
                No hay productos a granel. Andá a Productos, editá el producto de origen y marcá
                "Producto a granel (se puede fraccionar)".
              </p>
            )}
            {parent && (
              <p className="text-xs text-gray-400 mt-1">
                Stock del padre: {fmtNum(parentStockKg)} kg · Costo por kg: {formatPrice(parentCostPerKg)}
              </p>
            )}
            {isMulti && parent && parentFractions.length > 0 && (
              <div className="mt-2 flex flex-wrap items-center gap-1.5">
                <span className="text-xs text-gray-500">Ya tiene:</span>
                {parentFractions.map((f) => (
                  <span
                    key={f.id}
                    title={f.name}
                    className="text-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-600"
                  >
                    {f.grams >= 1000 && f.grams % 100 === 0 ? `${f.grams / 1000} kg` : `${f.grams} g`}
                  </span>
                ))}
                <span className="text-xs text-gray-400">— agregá abajo las que falten</span>
              </div>
            )}
          </div>

          {/* Paquete nuevo o producto que ya está cargado en el sistema */}
          {!editing && (
            <div className="grid grid-cols-2 gap-2">
              <button
                type="button"
                onClick={() => { setMode('new'); setLinkedProduct(null); }}
                className={`px-3 py-2 rounded-lg border-2 text-sm font-medium transition-colors ${
                  mode === 'new'
                    ? 'border-primary-500 bg-primary-50 text-primary-700'
                    : 'border-gray-200 text-gray-600 hover:border-gray-300'
                }`}
              >
                Crear paquete nuevo
              </button>
              <button
                type="button"
                onClick={() => setMode('existing')}
                className={`px-3 py-2 rounded-lg border-2 text-sm font-medium transition-colors ${
                  mode === 'existing'
                    ? 'border-primary-500 bg-primary-50 text-primary-700'
                    : 'border-gray-200 text-gray-600 hover:border-gray-300'
                }`}
              >
                Usar un producto que ya tengo
              </button>
            </div>
          )}

          {!editing && mode === 'existing' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                Producto del catálogo *
              </label>
              <ProductPicker
                products={linkableProducts}
                selected={linkedProduct ?? undefined}
                placeholder="Buscar el producto a asociar"
                onSelect={onSelectLinked}
                onClear={() => setLinkedProduct(null)}
              />
              <p className="text-xs text-gray-500 mt-1">
                No se crea un producto nuevo: a este se le asigna el producto a granel como origen.
                Deja de tener stock propio y pasa a descontar del padre, y su costo se calcula solo.
              </p>
            </div>
          )}

          {editing && (
            <Input
              label="Nombre del paquete *"
              placeholder="Ej: Alimento perro adulto 900 g"
              value={form.name}
              onChange={(e) => updateForm({ name: e.target.value })}
            />
          )}

          {/* Alta múltiple: todas las presentaciones del mismo padre de una sola vez */}
          {isMulti && (
            <div className="space-y-3">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <label className="text-sm font-medium text-gray-700">Presentaciones</label>
                <div className="flex flex-wrap gap-1.5">
                  {GRAM_PRESETS.map((g) => (
                    <button
                      key={g}
                      type="button"
                      onClick={() => addRow(g)}
                      className="text-xs px-2 py-1 rounded-lg bg-primary-50 text-primary-700 hover:bg-primary-100 font-medium"
                    >
                      + {g >= 1000 ? `${g / 1000} kg` : `${g} g`}
                    </button>
                  ))}
                  <button
                    type="button"
                    onClick={() => addRow()}
                    className="text-xs px-2 py-1 rounded-lg bg-gray-100 text-gray-700 hover:bg-gray-200 font-medium"
                  >
                    + Otra
                  </button>
                </div>
              </div>

              {rows.length === 0 && (
                <p className="text-sm text-gray-400 text-center py-4 border border-dashed border-gray-200 rounded-xl">
                  Agregá las presentaciones que vas a vender de este producto.
                </p>
              )}

              {rows.map((row) => {
                const g = rowGrams(row);
                const price = num(row.price);
                const cost = parentCostPerKg * (g / 1000);
                const profit = price - cost;
                const markup = cost > 0 ? (profit / cost) * 100 : 0;
                const packages = g > 0 ? Math.floor(parentStockKg / (g / 1000)) : 0;

                return (
                  <div key={row.id} className="border border-gray-200 rounded-xl p-3 space-y-2">
                    <div className="grid grid-cols-12 gap-2">
                      <div className="col-span-12 md:col-span-12">
                        <input
                          type="text"
                          placeholder="Nombre del paquete"
                          value={row.name}
                          onChange={(e) => updateRow(row.id, { name: e.target.value, nameTouched: true })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:border-primary-400"
                        />
                      </div>
                      <div className="col-span-5 md:col-span-4">
                        <label className="block text-[11px] text-gray-500 mb-0.5">Contenido</label>
                        <input
                          type="text"
                          inputMode="decimal"
                          placeholder="500"
                          value={row.amount}
                          onChange={(e) => onRowSizeChange(row, { amount: e.target.value })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:border-primary-400"
                        />
                      </div>
                      <div className="col-span-3 md:col-span-3">
                        <label className="block text-[11px] text-gray-500 mb-0.5">Unidad</label>
                        <select
                          value={row.unit}
                          onChange={(e) => onRowSizeChange(row, { unit: e.target.value as 'g' | 'kg' })}
                          className="w-full px-2 py-2 border border-gray-300 rounded-lg text-sm bg-white focus:outline-none focus:border-primary-400"
                        >
                          <option value="g">gramos</option>
                          <option value="kg">kilos</option>
                        </select>
                      </div>
                      <div className="col-span-4 md:col-span-4">
                        <label className="block text-[11px] text-gray-500 mb-0.5">Precio de venta</label>
                        <input
                          type="text"
                          inputMode="decimal"
                          placeholder="0"
                          value={row.price}
                          onChange={(e) => updateRow(row.id, { price: e.target.value })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:border-primary-400"
                        />
                      </div>
                      <div className="col-span-12 md:col-span-1 flex justify-end md:items-end">
                        <button
                          type="button"
                          onClick={() => removeRow(row.id)}
                          title="Quitar"
                          className="p-2 rounded-lg bg-red-50 text-red-500 hover:bg-red-100"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </div>

                    <div className="flex flex-wrap items-center justify-between gap-2 text-xs">
                      <span className="text-gray-500">
                        {g > 0 && parent ? (
                          <>
                            Costo {formatPrice(cost)}
                            {price > 0 && (
                              <>
                                {' · '}
                                <span className={profit >= 0 ? 'text-green-600' : 'text-red-600'}>
                                  ganancia {formatPrice(profit)} ({markup.toFixed(0)}%)
                                </span>
                              </>
                            )}
                            {' · '}salen {packages} con el stock actual
                          </>
                        ) : (
                          'Cargá los gramos y el precio'
                        )}
                      </span>
                      {g > 0 && existingGrams.has(g) && (
                        <span className="text-amber-600">
                          Ojo: este producto ya tiene una presentación de{' '}
                          {g >= 1000 && g % 100 === 0 ? `${g / 1000} kg` : `${g} g`}
                        </span>
                      )}
                      {row.barcode ? (
                        <span className="text-gray-500 font-mono">
                          {row.barcode}{' '}
                          <button
                            type="button"
                            onClick={() => updateRow(row.id, { barcode: '' })}
                            className="text-red-500 hover:text-red-700 font-sans"
                          >
                            quitar
                          </button>
                        </span>
                      ) : (
                        <button
                          type="button"
                          onClick={() => updateRow(row.id, { barcode: genBarcode() })}
                          className="px-2 py-1 rounded-lg bg-amber-50 text-amber-700 hover:bg-amber-100 font-medium"
                          title="Generar código de barras interno para este paquete"
                        >
                          Generar código de barras
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {!isMulti && (
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Gramos por paquete *</label>
              <input
                type="number"
                min="0"
                step="1"
                placeholder="900"
                value={form.grams}
                onChange={(e) => updateForm({ grams: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white focus:outline-none focus:border-primary-400"
              />
              <p className="text-xs text-gray-400 mt-1">Cuántos gramos tiene cada paquete.</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Precio de venta *</label>
              <input
                type="number"
                min="0"
                step="any"
                placeholder="0"
                value={form.price}
                onChange={(e) => updateForm({ price: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white focus:outline-none focus:border-primary-400"
              />
            </div>
          </div>
          )}

          {!isMulti && (
          <div className="grid grid-cols-2 gap-3">
            <Input
              label="Código interno (opcional)"
              placeholder="Ej: ALI-900"
              value={form.code}
              onChange={(e) => updateForm({ code: e.target.value })}
            />
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                Código de barras (propio)
              </label>
              <div className="flex items-center gap-2">
                <input
                  type="text"
                  placeholder="Escaneá o generá"
                  value={form.barcode}
                  onChange={(e) => updateForm({ barcode: e.target.value })}
                  className="flex-1 min-w-0 px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white focus:outline-none focus:border-primary-400"
                />
                <button
                  type="button"
                  onClick={() => updateForm({ barcode: genBarcode() })}
                  className="flex-shrink-0 px-3 py-2 rounded-lg bg-amber-50 text-amber-700 hover:bg-amber-100 text-sm font-medium"
                  title="Generar código de barras interno"
                >
                  Generar
                </button>
              </div>
              <p className="text-xs text-gray-400 mt-1">Cada paquete lleva su propio código (no se hereda del padre).</p>
            </div>
          </div>
          )}

          {/* Costo y ganancia */}
          {!isMulti && gramsNum > 0 && parent && (
            <div className="bg-gray-50 border border-gray-200 rounded-xl p-4 space-y-1.5">
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Costo del paquete (auto desde el padre):</span>
                <span className="font-semibold text-gray-800">{formatPrice(fracCost)}</span>
              </div>
              {priceNum > 0 && (
                <>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Ganancia:</span>
                    <span className={`font-semibold ${fracProfit >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                      {formatPrice(fracProfit)}
                    </span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">% de ganancia:</span>
                    <span className={`font-bold ${fracProfit >= 0 ? 'text-green-700' : 'text-red-600'}`}>
                      {fracMarkupPct.toFixed(0)}% sobre costo
                      <span className="text-gray-400 font-normal"> · {fracMarginPct.toFixed(0)}% margen</span>
                    </span>
                  </div>
                </>
              )}
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Paquetes posibles con el stock actual:</span>
                <span className="font-semibold text-gray-700">{possiblePackages}</span>
              </div>
              {parentCostPerKg <= 0 && (
                <p className="text-xs text-amber-600">
                  El producto padre no tiene costo cargado: el costo mostrado es 0.
                </p>
              )}
            </div>
          )}

          <p className="text-xs text-gray-400">
            Los fraccionados no llevan stock propio: al venderlos se descuentan los gramos del stock
            del padre. El costo se actualiza solo si cambia el costo del padre.
          </p>

          {/* Acciones */}
          <div className="flex gap-3 pt-2">
            <Button variant="secondary" className="flex-1" onClick={() => setIsModalOpen(false)}>
              Cancelar
            </Button>
            <Button
              className="flex-1"
              disabled={!isValid || savingRows || createProduct.isPending || updateProduct.isPending}
              onClick={handleSave}
            >
              <Check className="h-5 w-5 mr-2" />
              {editing
                ? 'Guardar cambios'
                : isMulti
                  ? (validRows.length > 1 ? `Crear ${validRows.length} fraccionados` : 'Crear fraccionado')
                  : 'Asociar producto'}
            </Button>
          </div>
        </div>
      </Modal>

      {/* Modal eliminar */}
      <Modal isOpen={!!deleteConfirm} onClose={() => setDeleteConfirm(null)} title="Eliminar fraccionado">
        <div className="space-y-4">
          <p>
            ¿Eliminar el fraccionado <strong>{deleteConfirm?.name}</strong>?
          </p>
          <div className="flex gap-3">
            <Button variant="secondary" className="flex-1" onClick={() => setDeleteConfirm(null)}>
              No
            </Button>
            <Button variant="danger" className="flex-1" onClick={handleDelete} disabled={deleteProduct.isPending}>
              Sí, eliminar
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}

/**
 * Buscador de productos: reemplaza a los desplegables con todo el catálogo, que con
 * muchos productos son inmanejables. Filtra por nombre o código de barras.
 */
function ProductPicker({
  products,
  selected,
  placeholder,
  onSelect,
  onClear,
}: {
  products: Product[];
  selected?: Product;
  placeholder: string;
  onSelect: (p: Product) => void;
  onClear: () => void;
}) {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);

  const results = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (q.length < 2) return products.slice(0, 8);
    return products
      .filter((p) => p.name.toLowerCase().includes(q) || (p.barcode || '').toLowerCase().includes(q))
      .slice(0, 8);
  }, [products, query]);

  if (selected) {
    return (
      <div className="flex items-center gap-2 px-3 py-2 border border-primary-200 bg-primary-50 rounded-lg">
        <Package className="h-4 w-4 text-primary-600 flex-shrink-0" />
        <span className="text-sm text-gray-800 truncate flex-1">{selected.name}</span>
        <button
          type="button"
          title="Cambiar"
          onClick={() => { onClear(); setQuery(''); }}
          className="text-xs text-primary-700 hover:text-primary-900 px-2 py-1 rounded hover:bg-primary-100"
        >
          Cambiar
        </button>
      </div>
    );
  }

  return (
    <div className="relative">
      <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-gray-400 pointer-events-none" />
      <input
        type="text"
        value={query}
        onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
        onFocus={() => setOpen(true)}
        onBlur={() => setTimeout(() => setOpen(false), 150)}
        placeholder={placeholder}
        className="w-full pl-8 pr-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:border-primary-400"
      />
      {open && (
        <div className="absolute z-30 left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-56 overflow-y-auto">
          {results.length === 0 ? (
            <p className="px-3 py-2 text-sm text-gray-400">
              {query.trim() ? `Sin resultados para "${query.trim()}"` : 'No hay productos disponibles'}
            </p>
          ) : (
            results.map((p) => (
              <button
                key={p.id}
                type="button"
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => { onSelect(p); setQuery(''); setOpen(false); }}
                className="w-full flex items-center justify-between gap-2 px-3 py-2 text-left hover:bg-gray-50"
              >
                <span className="text-sm text-gray-800 truncate">{p.name}</span>
                {p.sale_price != null && (
                  <span className="text-xs text-gray-500 flex-shrink-0">{formatPrice(p.sale_price)}</span>
                )}
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
}
